import React, { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
    Box,
    Button,
    Typography,
    Stack,
    Link
} from "@mui/material";
import {useNavigate} from "react-router";
import { useMutation } from '@tanstack/react-query';
import { DefaultError } from '@tanstack/query-core';
import {AxiosError} from 'axios';
import {login, LoginPayload} from "../../service/auth.service";
import {useAuthStore} from "../../store/auth.store";
import {enqueueSnackbar} from "notistack";
import { User } from "../../types/User";
import { TextInput } from "../form/TextInput";
import { parseQueryParams } from "../../utils/UrlUtils";




export function LoginForm({setLoading}:{setLoading: (b: boolean) => void}) {
    const initialValues: LoginPayload = useMemo(() => ({
        requestCode: false,
        login: "",
        password: "",
        emailToUpdate: null,
        emailCode: null,
        auth: parseQueryParams(document.location.toString()).ql || null
    }), []);
    const navigate = useNavigate();
    const authStore = useAuthStore();
    const [step, setStep] = useState<"login" | "email" | 'code'>("login")

    const handleSubmit = (values: LoginPayload) => {
        mutation.mutate(values);
    };
    const [qlSent, setQlSent] = useState<boolean>(false);

    const formik = useFormik<LoginPayload>({
        initialValues: initialValues,
        validationSchema: (() => {
            if(qlSent) {
                return Yup.object().shape({
                    emailToUpdate: Yup.string().email("Seems it is not valid email").notRequired(),
                    emailCode: Yup.string().notRequired(),
                })
            } else {
                return Yup.object().shape({
                    login: Yup.string().required("Login is required"),
                    password: Yup.string().required("Password is required"),
                    emailToUpdate: Yup.string().email("Seems it is not valid email").notRequired(),
                    emailCode: Yup.string().notRequired(),
                });
            }
        })(),
        onSubmit: handleSubmit,
    })

    const mutation = useMutation<{jwt:string, user: User}, DefaultError, LoginPayload>({
        mutationFn: (payload) => login(payload).then(r => r.data),
        onSuccess: (data) => {
            navigate("/")
            authStore.setUser(data.user)
            authStore.setJwt(data.jwt);
            authStore.setShowLogin(false);
        },
        onError: (error) => {
            //@ts-ignore
            if(error && error.isAxiosError) {
                const er = error as AxiosError;
                if(er.status) {
                    if(er.status === 401) {
                        enqueueSnackbar({ message: "Login or password incorrect", variant: 'error' })
                    } else if(er.status === 409) {
                        enqueueSnackbar({ message: "Such email address already registered in the system", variant: 'error' })
                    }  else if(er.status === 423) {
                        setStep('email');
                        enqueueSnackbar({message: 'Enter your email', variant: 'success'})
                    } else if(er.status === 425) {
                        enqueueSnackbar({message: 'Check your email', variant: 'success'})
                        setStep('code')
                    } else {
                        enqueueSnackbar({ message: "Failed to login " + er.message, variant: 'error' })
                    }
                } else {
                    if(er.code === 'ERR_NETWORK') {
                        enqueueSnackbar({message: "Please check your internet connection", variant: 'error'})
                    } else {
                        enqueueSnackbar({message: "Failed to login: " + er.message, variant: 'error'})
                    }
                }
            } else {
                enqueueSnackbar({message: "Failed to login " + error.message, variant: 'error'})
            }
        }
    });


    useEffect(() => {
        if(qlSent) {
            return
        }
        setQlSent(true);
        const params = parseQueryParams(document.location.toString());
        if(params.ql) {
            mutation.mutate(initialValues)
        }
    }, [mutation, qlSent, initialValues]);

    useEffect(() => {
        setLoading(mutation.isPending)
    }, [setLoading, mutation.isPending]);


    return (
        <Box sx={{ maxWidth: 450,  minWidth: 350, margin: "auto" }}>
            <Typography variant="h5" gutterBottom textAlign={"center"}>
                Welcome to goodplacegeorgia.com
            </Typography>
            <Typography gutterBottom textAlign={"center"} className={"mb-15-i"}>
                A place where real estate is sold
            </Typography>
            <form onSubmit={formik.handleSubmit}>
                {step === 'login' && <Box mb={2}>
                    <TextInput
                        fullWidth
                        name="login"
                        label="Login"
                        formik={formik}
                    />
                </Box>}
                {step === 'login' && <Box mb={2}>
                    <TextInput
                        fullWidth
                        name="password"
                        label="Password"
                        type="password"
                        formik={formik}
                    />
                </Box>}

                {step === 'email' && <Box mb={2}>
                    <div>Your account was created automatically and currently it is blocked</div>
                    <div>To unlock your account and proceed to application, please provide your email address</div>
                    <div>Verification code will be sent to provided address</div>
                </Box>}
                {step === 'email' && <Box mb={2}>
                    <TextInput
                        fullWidth
                        name="emailToUpdate"
                        label="Your email"
                        type="text"
                        formik={formik}
                    />
                </Box>}
                {step === 'code' && <Box mb={2}>
                    <div>Secret code was sent to your email address</div>
                    <div>Please check your email box and enter code below</div>
                    <div>If you can't see email, please check 'junk' or 'spam' folders</div>
                </Box>}
                {step === 'code' && <Box mb={2}>
                    <TextInput
                        fullWidth
                        name="emailCode"
                        label="Secret Code"
                        type="text"
                        formik={formik}
                    />
                </Box>}
                {step === 'code' && <Stack direction={'row'} justifyContent={'space-between'}>
                    <Box mb={2}>
                        <Button type={'button'} variant={'text'} onClick={() => setStep('email')}>
                            Change Email
                        </Button>
                    </Box>
                    <Box mb={2}>
                        <Button type={'button'} variant={'text'} onClick={() => {
                            handleSubmit({...formik.values, requestCode: true})
                        }}>
                            Resend Code
                        </Button>
                    </Box>
                </Stack>}
                <Stack flexDirection={"row"} justifyContent="space-between" alignItems="center">
                    {step === 'login' &&
                        <Button type="button" variant={'contained'} color="primary" onClick={() => {
                            authStore.setShowLogin(false)
                            authStore.setShowRegister(true)
                        }}>
                            Register
                    </Button>}
                    {step !== 'login' && <Button type="button" variant="contained" color="primary" onClick={() =>
                        setStep('login')
                    }>
                        Back
                    </Button>}
                    <Button type="submit" disabled={mutation.isPending} variant="contained" color="primary">
                        {step === 'login' ? 'Sign in' : 'Next'}
                    </Button>
                </Stack>
                <Stack alignItems={"center"}>
                    <Link href={"/#"} onClick={(e) => {
                        e.preventDefault()
                        authStore.setShowLogin(false)
                        authStore.setShowResetPassword(true)
                    }}>
                        Forgot password?
                    </Link>
                </Stack>
            </form>
        </Box>
    );
}